import { defaultTtsVoice, DesignPromptDynamicType } from "@app/types";
import BaseNodeModel from "../BaseNodeModel";
import ObjectsUsedIn from "../../utilities/usedIn/ObjectsUsedIn";
import { PromptTextAreas } from "../../form_components";
import { getGlobalSettings } from "../../stores";
import { getDefaultChannelContent, setDeepProperty } from "../../utilities";
import { getInputsFromAllTexts } from "../../utilities/functions/prompt/get-inputs-from-all-texts";

export default class PromptNodeModel extends BaseNodeModel {
    /**
     * Prompt node model
     * @module PromptNodeModel
     * @param {string} nodeName Node display name
     * @param {number} x Node x coordinate on canvas
     * @param {number} y Node y coordinate on canvas
     * @param {Object} props Pre-defined node properties; if non-null, apply values to node properties
     * @param {string} id If non-null, use this ID as the node ID
     */
    constructor(nodeName = "UNTITLED_NODE", x = 0, y = 0, props = null, id = null, module) {
        super(nodeName, x, y, null, id);
        this.props.nodeTypeName = "Prompt";
        this.props.colorClass = "orange";
        this.props.iconClass = "message-square";
        this.DEFAULT_COLOR_CLASS = "orange";
        this.applyProps(props);
        this.refChangeSettings = this.refChangeSettings.bind(this);
        this.module = module;
        this.update = this.update.bind(this);
    }

    refChangeSettings() {
        window.apm.reactDOMComponents.render({
            name: 'SettingWidget',
            props: {
                serialized: window.apm.serialized,
                referenceablePrompts: window.apm.serialized.components.objects.filter((o) => {
                    return o._class === "PromptNodeModel" && o.props.config.isReferenceable;
                })
            },
            $el: $('#settings')
        });
    }

    setPromptText(value, lang){
        const { isOmniChannel } = window.apm.serialized.omniChannel;
        const { ttsOverrides } = window.apm.serialized.rootSettings.features;
        const { channel, ttsVoices } = getGlobalSettings();
        const ttsVoice = ttsVoices?.[lang];

        if (ttsOverrides && ttsVoice && ttsVoice !== defaultTtsVoice) {
            this.props.config = setDeepProperty(this.props.config, ['ttsOverrides', lang, ttsVoice, 'text'], value);
        } else {
            if (isOmniChannel) {
                if (!this.props.config?.channel?.[channel]) {
                    this.props.config = setDeepProperty(this.props.config, ['channel', channel], { ...getDefaultChannelContent(), [lang]: value });
                }
                this.props.config = setDeepProperty(this.props.config, ['channel', channel, lang], value);
            }
            this.props.config.text[lang] = value;
        }
    }

    getConfig(key) {
        if (key === "text") {
            if (!this.props.config.hasOwnProperty("text") || !this.props.config.text) {
                this.props.config.text = {};
            }
        }
        return super.getConfig(key);
    }

    setConfig(key, value, xtras = null) {
        const { isOmniChannel } = window.apm.serialized.omniChannel;

        if (key === "dynamicPromptType") {
            if (value === "TTP") {
                // DEVDES2-934 - set default ttpInterCharacterPauseMs when choosing dynamicPromptType
                if (this.props.config.ttpInterCharacterPauseMs === undefined) {
                    this.props.config.ttpInterCharacterPauseMs = "0";
                }
            } else {
                delete this.props.config.ttpInterCharacterPauseMs;
            }

            // Refresh property panel when changing what function the node is using
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        }
        if (key === "isExternalAudio") {
            if (value === true) {
                this.props.config.externalAudioPromptType = "EXTERNAL_AUDIO";
                document.dispatchEvent(new CustomEvent("ev-property-refresh"));
            }
            if (value === false ) {
                this.props.config.dynamicPromptType = "TTP";
                if (this.props.error.hasOwnProperty("inputValRules::externalAudioServiceUrl::noEmpty")) {
                    var id = this.props.parentComponentID ? this.props.parentComponentID: this.id;
                    this.removeErrorOnSwitchToggle(id, "inputValRules::externalAudioServiceUrl::noEmpty")
                }
                if (this.props.error.hasOwnProperty("inputValRules::externalAudioVariable::noEmpty")) {
                    var id = this.props.parentComponentID ? this.props.parentComponentID: this.id;
                    this.removeErrorOnSwitchToggle(id, "inputValRules::externalAudioVariable::noEmpty")
                }
                document.dispatchEvent(new CustomEvent("ev-property-refresh"));
                window.apm.postEventToCurrentView({ target: "request-refresh"});
            }
        }
        if (key === "externalAudioPromptType"&&value==="EXTERNAL_AUDIO_VAR") {
            if (this.props.error.hasOwnProperty("inputValRules::externalAudioServiceUrl::noEmpty")) {
                var id = this.props.parentComponentID ? this.props.parentComponentID: this.id;
                this.removeErrorOnSwitchToggle(id, "inputValRules::externalAudioServiceUrl::noEmpty")
            }
        }
        if (key === "externalAudioPromptType"&&value==="EXTERNAL_AUDIO") {
            if (this.props.error.hasOwnProperty("inputValRules::externalAudioVariable::noEmpty")) {
                var id = this.props.parentComponentID ? this.props.parentComponentID: this.id;
                this.removeErrorOnSwitchToggle(id, "inputValRules::externalAudioVariable::noEmpty")
            }
        }
        if(key === "allowFallbackDomain"){
            this.props.config.allowFallbackDomain = value;
        }
        if (key === "text") {

            this.setPromptText(value, xtras.lang);
            const currentCachedModule = window.apm.getCurrentDiagramWidget().props.node;
            const currentModule = window.apm.serialized.components.modules.find(m => m.id === currentCachedModule.id);
            const originalInputsLength = this.props.config.inputs?.length ?? 0;

            const inputsFromText = getInputsFromAllTexts({
                isOmniChannel,
                localVariables: currentModule.localVariables,
                node: this
            });

            const isDynamic = inputsFromText.length > 0;

            this.props.config.inputs = inputsFromText;
            this.props.config.dynamic = isDynamic;

            if (isDynamic && this.props.config.dynamicPromptType === "TTP") {
                if (this.props.config.ttpInterCharacterPauseMs === undefined) {
                    this.props.config.ttpInterCharacterPauseMs = "0";
                }
            } else {
                delete this.props.config.ttpInterCharacterPauseMs;
            }
            
            if(isDynamic && originalInputsLength === 0){
                this.props.config.allowFallbackDomain = true;
            }

            if(inputsFromText.length === 0){
                this.props.config.allowFallbackDomain = false;
            }

            document.dispatchEvent(new CustomEvent("ev-property-item-list-refresh"));
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
            document.dispatchEvent(new CustomEvent("ev-dialog-node-changed", { detail: this.serialize() }));
            return;
        }
        super.setConfig(key, value);
        this.update();
    }

    update() {
        document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        document.dispatchEvent(new CustomEvent("ev-dialog-node-changed", { detail: this.serialize() }));
    }

    removeErrorOnSwitchToggle(id,error) {
        const promptUsedIn = ObjectsUsedIn.getObjectUsedIn(id, window.apm.serialized);
        promptUsedIn[0].forEach(i => {
            const moduleIndex = window.apm.serialized.components.modules.findIndex(m => m.id === i);
            if (moduleIndex !== -1) {
                window.apm.serialized.components.modules[moduleIndex].versions[1].flow.nodes.forEach((node, index) => {
                    if (node.props.parentComponentID === id) {
                        delete window.apm.serialized.components.modules[moduleIndex].versions[1].flow.nodes[index].props.error[error];
                        window.apm.postEventToAllViews({ target: "request-refresh", event: { id: node.id, type: "node", upDatedField: "externalAudioServiceURL", props: node.props } });
                    }
                })
            }
        })
    }

    getChannelConfigViews() {
        return [<PromptTextAreas context={this} setConfig={this.setConfig} update={this.update} />];
    }

    getConfigViews() {
        const configView = this.getChannelConfigViews();

        configView.push(
            { type: "Switch", key: "repeatable", label: this.getPropLabel("repeatable"), checked: false },
            {
                type: "Switch",
                key: "isReferenceable",
                label: this.getPropLabel("isReferenceable"),
                checked: false,
                callback: this.refChangeSettings
            },
            { type: "Switch", key: "isExternalAudio", label: this.getPropLabel("externalAudio"), checked: false }
        );
        
        if (this.getConfig('isExternalAudio')) {
            configView.push(
                {},{
                type: "Dropdown", key: "externalAudioPromptType", label: "External Audio Prompt Type", optionName: this.getPropLabel("select_dynamic_prompt_type"),
                options: [
                    { name: "Extenal Audio", value: "EXTERNAL_AUDIO" },
                    { name: "External Audio Variable", value: "EXTERNAL_AUDIO_VAR" },
                ]
            });
        }
        if (this.getConfig("isExternalAudio")&&this.getConfig('externalAudioPromptType')==="EXTERNAL_AUDIO") {
            configView.push(
                {
                    type: "Input", label: this.getPropLabel("external_Audio_Service_Url"), key: "externalAudioServiceUrl", valRules: { "noEmpty": true },tooltip:"Instructs the Process Engine to assemble a URL to a prompt, using the value of this property as its base"
                }
            );
        }
        if (this.getConfig("isExternalAudio")&&this.getConfig('externalAudioPromptType')==="EXTERNAL_AUDIO_VAR") {
            configView.push(
                {},
                { type: "Input", label: this.getPropLabel("external_audio_variable"), key: "externalAudioVariable", suggestion: "variable",valRules: { "noEmpty": true } ,tooltip:"The given variable is expected to contain a fully qualified URL to the prompt to be played"},
            );
        }
        configView.push(
            {
                type: "Dropdown", key: "bargeable", label: this.getPropLabel("bargeable"), optionName: this.getPropLabel("bargeable_opt"),
                options: [
                    { name: "Disabled", value: "DISABLED" }, { name: "DTMF Only", value: "DTMF_ONLY" }, { name: "Enabled", value: "ENABLED" }
                ]
            });

        if (this.getConfig("dynamic")&&!this.props.config.isExternalAudio) {
            configView.push(
                { type: "hr" },
                {
                    type: "Dropdown", key: "dynamicPromptType", label: this.getPropLabel("dynamicPromptType"), optionName: this.getPropLabel("select_dynamic_prompt_type"),
                    options: [
                        { name: "Full TTS", value: DesignPromptDynamicType.fullTts },
                        { name: "Substitution TTS", value: DesignPromptDynamicType.substitutionTts },
                        { name: "TTP", value: DesignPromptDynamicType.ttp },
                        { name: "Full TTS with SSML", value: DesignPromptDynamicType.ttsWithSsml }

                    ]
                },
                { type: "Switch", key: "allowFallbackDomain", label: this.getPropLabel("allowFallbackDomain"), checked: true,tooltip: this.getPropLabel("allow_fallback_domain_tooltip")}
            );
            if (this.getConfig("dynamicPromptType") === "TTP") {
                configView.push(
                    { type: "Input", label: this.getPropLabel("inter_character_pause_ms"), key: "ttpInterCharacterPauseMs", inputRules: { "numberOnly": true }, tooltip: this.getPropLabel("inter_character_pause_tooltip") },
                );
            }
            if (this.getConfig("dynamicPromptType") === "TTS" || this.getConfig("dynamicPromptType") === "SUBSTITUTION_TTS") {
                configView.push(
                    { type: "Slider", key: "readbackSpeed", enabledKey: "dynamic", label: this.getPropLabel("readback_speed"), min: -99, max: 99, labelSuffix: "%" }
                );
            }
            configView.push(
                { type: "ItemList", label: this.getPropLabel("variables_referenced"), key: "inputs", view: "dynamicVariableReference", updateOnly: true }
            );
        }
        if (!this.getConfig("isExternalAudio") && !this.getConfig("dynamic")) {
            configView.push(
                { type: "hr" },
                { type: "ItemList", label: this.getPropLabel("variables_referenced"), key: "inputs", view: "variableReference", readOnly: true }
            );
        }

        return [
            ...this.getConfigViewNameField(),
            ...configView,
            { type: "hr" },
            { type: "Textarea", label: this.getPropLabel("note"), key: "note" }
        ];
    }
}
